/**
 * XPEN-1622: Helper for invoice modal error block visibility and finalization_errors.
 * XPEN-1990: Finalization error display uses only the top-level finalization_errors array;
 * legacy parsing of status_details for finalization errors has been removed.
 * The error block (inline alert) must be hidden when the invoice is in Need_Approval or Validated;
 * it may be shown for Need_Attention or Invalid when there is status_details content.
 * The partner-errors alert icon (HoverInfoCard) is shown only when status is Validated and there is
 * partner error content from finalization_errors; all other statuses (including Finalized) hide the icon.
 * xpen-1955: Used to disable modal action buttons when the invoice is in Finalized status.
 */

/**
 * Returns true if the invoice error block may be shown based on status.
 * False for Need_Approval and Validated (user has moved past "needs attention");
 * true for Need_Attention and Invalid (error/attention states).
 *
 * @param {string} status - Invoice status (e.g. 'Need_Attention', 'Validated')
 * @returns {boolean}
 */
export function shouldShowInvoiceErrorBlock(status) {
  if (!status) return false;
  if (status === 'Need_Approval' || status === 'Validated') return false;
  if (status === 'Need_Attention' || status === 'Invalid') return true;
  return false;
}

/**
 * Normalizes finalization_errors for display per backend contract (XPEN-1622).
 * Backend now returns finalization_errors exactly as stored in DB (absent, null, string (legacy), or array of mixed types).
 * This is the SINGLE place that normalizes for all UI display.
 * Safe for null/undefined invoice (returns []). Always returns string[] (never throws).
 *
 * @param {{ finalization_errors?: unknown } | null | undefined} invoice - Invoice object (may be null/undefined).
 * @returns {string[]}
 */
export function getFinalizationErrors(invoice) {
  const raw = invoice?.finalization_errors;
  if (Array.isArray(raw)) {
    return raw.filter((e) => typeof e === 'string'); // defensive + new array (immutable)
  }
  if (typeof raw === 'string') {
    return [raw];
  }
  return []; // missing, null, undefined, number, object, etc.
}

/**
 * Returns true iff the invoice has at least one finalization (partner) error.
 *
 * @param {{ finalization_errors?: unknown } | null | undefined} invoice - Invoice object
 * @returns {boolean}
 */
export function hasFinalizationErrors(invoice) {
  return getFinalizationErrors(invoice).length > 0;
}

/**
 * Returns the most recent finalization error for display (e.g. dashboard tooltip).
 * Backend order is oldest-first; last element = most recent. Single source of truth is
 * finalization_errors; no legacy status_details. Safe for null/undefined invoice (returns '').
 *
 * @param {{ finalization_errors?: unknown } | null | undefined} invoice - Invoice object
 * @returns {string}
 */
export function getLastFinalizationError(invoice) {
  const fe = getFinalizationErrors(invoice);
  return fe.length > 0 ? fe[fe.length - 1] : '';
}

/**
 * Returns the list of partner (finalization) error strings to show in the HoverInfoCard.
 * Uses finalization_errors only; legacy status_details parsing for finalization has been removed (XPEN-1990).
 * Guard: if !invoice return [] (safe when called with null/undefined).
 *
 * @param {{ finalization_errors?: unknown } | null | undefined} invoice - Invoice object
 * @returns {string[]}
 */
export function getPartnerErrorsForDisplay(invoice) {
  if (!invoice) return [];
  return getFinalizationErrors(invoice);
}

/**
 * Returns true iff there are partner errors to show (from finalization_errors only; no legacy status_details). Safe for null/undefined via guard in getPartnerErrorsForDisplay.
 *
 * @param {{ finalization_errors?: unknown } | null | undefined} invoice - Invoice object
 * @returns {boolean}
 */
export function hasPartnerErrorsToShow(invoice) {
  return getPartnerErrorsForDisplay(invoice).length > 0;
}

/**
 * Returns true only when the invoice status is 'Validated' and there is at least one partner error
 * to show (from finalization_errors only; no legacy status_details). Used by the invoice modal to control
 * HoverInfoCard icon visibility. Safe for null/undefined. Defensive: returns false for Finalized
 * even if partner data is present (backend should clear it; we hide the icon regardless).
 *
 * @param {{ status?: string; finalization_errors?: unknown } | null | undefined} invoice - Invoice object (may be null/undefined).
 * @returns {boolean}
 */
export function shouldShowPartnerErrorsIcon(invoice) {
  return invoice?.status === 'Validated' && hasPartnerErrorsToShow(invoice);
}

/**
 * Returns true when the invoice is in Finalized status; otherwise false.
 * Safe for null/undefined invoice. Used to disable modal action buttons (Validate,
 * Request Approval, Approve, Disapprove, Restart processing, Restart post processing, Redact).
 * (xpen-1955)
 *
 * @param {{ status?: string } | null | undefined} invoice - The invoice object (may be null/undefined).
 * @returns {boolean}
 */
export function isInvoiceFinalized(invoice) {
  return invoice?.status === 'Finalized';
}

/**
 * Returns true when changing supplier/payee is allowed to reset invoice default fields
 * (GL, unit, office, billable flags, due date, line item defaults). For invoices at
 * Need_Approval or later workflow steps, we keep existing invoice defaults unchanged.
 *
 * @param {{ status?: string } | null | undefined} invoice - The invoice object (may be null/undefined).
 * @returns {boolean}
 */
export function shouldResetSupplierDefaultsOnPayeeChange(invoice) {
  const status = invoice?.status;
  const statusesThatMustKeepCurrentDefaults = new Set(['Need_Approval', 'Completed', 'Approved', 'Validated', 'Finalized']);
  return !statusesThatMustKeepCurrentDefaults.has(status);
}

/**
 * Returns true when the dashboard invoice list may open the invoice editor modal.
 * Pending and Processing invoices are still being handled by AI and should not
 * be opened from the list yet.
 *
 * @param {{ status?: string } | null | undefined} invoice - The invoice object.
 * @returns {boolean}
 */
export function canOpenInvoiceEditorFromDashboard(invoice) {
  return !['Pending', 'Processing'].includes(invoice?.status);
}
